import type { PerpsBalance } from '@infinex/types';

/**
 * True when the row IS spot USDC cash. Matches on the held asset only —
 * `quoteToken` must not be used here: Hyperliquid spot pairs are USDC-quoted,
 * so a non-USDC holding (e.g. HYPE) can legitimately carry `quoteToken:
 * 'USDC'` and must not be classified as USDC cash (it would be valued 1:1
 * and lose its mark price). Shared by {@link selectWithdrawableUsdc} and
 * `selectSpotHoldings` so the gate, value displays and withdraw dialog agree
 * on what counts as USDC.
 */
export function isUsdcSpotToken(balance: PerpsBalance): boolean {
  return (
    balance.productKind === 'spot' &&
    balance.balanceKind === 'spot_token' &&
    balance.asset === 'USDC'
  );
}

export interface WithdrawableUsdc {
  /** Perp margin available (the withdrawable figure for legacy/non-HL providers). */
  perpAvailable: number;
  /** Free spot USDC (total − hold); the unified withdrawable for Hyperliquid. */
  spotAvailable: number;
  /** Withdrawable USDC: the larger of perpAvailable and spotAvailable. */
  withdrawable: number;
}

/**
 * USDC a user can withdraw from a Hyperliquid-style account.
 *
 * For a unified account the USDC is a single pool that backs both spot and perp;
 * the spot clearinghouse `available` (total − hold) is the real withdrawable and
 * the perp `withdrawable` is ~0 / not meaningful. For a legacy (non-unified) or
 * non-Hyperliquid provider the balance instead sits in perp margin. Taking the
 * larger of the two covers both cases without double-counting, and `withdraw3`
 * draws from whichever pool holds the funds — no spot → perp transfer.
 */
export function selectWithdrawableUsdc(
  balances: readonly PerpsBalance[]
): WithdrawableUsdc {
  let perpAvailable = 0;
  let spotAvailable = 0;

  for (const balance of balances) {
    if (
      balance.productKind === 'perp' &&
      balance.balanceKind === 'perp_margin'
    ) {
      perpAvailable += Number(balance.available ?? 0);
    } else if (isUsdcSpotToken(balance)) {
      spotAvailable += Number(balance.available ?? 0);
    }
  }

  return {
    perpAvailable,
    spotAvailable,
    withdrawable: Math.max(perpAvailable, spotAvailable),
  };
}
